import { useMemo } from 'react';
import { RegisteredStep, BaseTour } from '../types';
import { AppTour } from '../tours/AppTour';
import { TourPersistenceState } from './useTourPersistence';

interface UseTourStepsReturn {
  activeSteps: RegisteredStep[];
  activeTour: BaseTour;
}

/**
 * Check if a DOM element exists and is visible.
 */
function isElementVisible(selector: string): boolean {
  const element = document.querySelector(selector);
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);

  return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none' && parseFloat(style.opacity) > 0;
}

/**
 * Filter steps to only include those with visible DOM targets.
 */
function filterVisibleSteps(steps: RegisteredStep[]): RegisteredStep[] {
  return steps.filter((step) => {
    if (!step.target || typeof step.target !== 'string') return true;
    return isElementVisible(step.target);
  });
}

/**
 * Computes the active steps for the current tour.
 *
 * Single place for all step computation and DOM visibility checks.
 * Replaces the 3 duplicate check locations in the old TourProvider.
 */
export function useTourSteps(
  appTour: AppTour,
  persistenceState: TourPersistenceState,
  manualTourId: string | null,
  tourVersion: number,
  locationPathname: string,
  stepOffset: number,
  isPaused: boolean
): UseTourStepsReturn {
  return useMemo<UseTourStepsReturn>(() => {
    const { completedTours } = persistenceState;
    let steps: RegisteredStep[] | null = null;
    let tour: BaseTour = appTour;

    // 1. Priority: Manual Tour (manually triggered or from sequence)
    if (manualTourId) {
      const allTours = appTour.genTours([]);
      const targetTour = allTours.find((t) => t.id === manualTourId);

      if (targetTour) {
        // For manual tours, get all steps (don't filter by completedTours)
        const result = targetTour.getNextSteps([]);
        tour = result.tour;
        steps = result.steps;
      }
    }

    // 2. Fallback: Auto Tour (find first uncompleted tour)
    if (!steps && !manualTourId) {
      // getNextSteps uses excludeIds for both genSteps (step IDs) and genTours (tour IDs).
      // completedTours contains tour IDs (e.g. "audit-dashboard"), not step IDs
      // (e.g. "audit-dashboard-refresh"), so genSteps won't filter them out.
      // Instead, find the first uncompleted sub-tour and get its steps directly.
      const allTours = appTour.genTours(completedTours);
      for (const subTour of allTours) {
        const result = subTour.getNextSteps([]);
        if (result.steps && result.steps.length > 0) {
          tour = result.tour;
          steps = result.steps;
          break;
        }
      }
    }

    if (!steps || steps.length === 0) {
      return { activeSteps: [], activeTour: tour };
    }

    // Apply step index offset if resuming from a paused position
    const offsetSteps = stepOffset > 0 ? steps.slice(stepOffset) : steps;

    // Single DOM visibility check (replaces 3 duplicate locations)
    const visibleSteps = filterVisibleSteps(offsetSteps);

    // Total for the progress counter: steps already seen + steps remaining
    const displayTotal = stepOffset + visibleSteps.length;

    // Step 0: auto-open tooltip when tour starts fresh; show beacon only when paused.
    // Embed absolute display index/total into step.data so CustomTooltip can show
    // the correct "3 / 5" counter even when the steps array is sliced.
    const finalSteps = visibleSteps.map((step, index) => ({
      ...step,
      disableBeacon: index === 0 ? !isPaused : false,
      data: {
        ...(step.data ?? {}),
        displayIndex: stepOffset + index,
        displayTotal,
      },
    }));

    return { activeSteps: finalSteps, activeTour: tour };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourVersion, persistenceState.completedTours, appTour, locationPathname, manualTourId, stepOffset, isPaused]);
}
